import { createElement, ChangeEvent, DragEvent, FC, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Colors } from 'src/components/theme'
import { Typography } from '@mui/material'
import { Upload } from '@emotion-icons/heroicons-outline/Upload'
import { EmotionIcon } from '@emotion-icons/emotion-icon'

const FILE_TYPES = {
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.txt': 'text/plain',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.svg': 'image/svg+xml',
  '.zip': 'application/zip',
}

const Container = styled.div`
  padding: 48px 16px;
  border: 1px dashed ${Colors.blueDark};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
`

const Button = styled.button`
  background-color: white;
  border: 1px solid ${Colors.blueDark};
  color: ${Colors.blueDark};
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  padding: 4px 8px;
  cursor: pointer;
`

const UploadWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-direction: column;
`

const UploadDescriptions = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const FileInput = styled.input`
  display: none;
`

type FileUploadContentProps = {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  accept?: string
  uploadText?: string
  uploadButtonText?: string
  icon?: EmotionIcon
}

const UploadContent: FC<FileUploadContentProps> = ({ onChange, accept, uploadButtonText, uploadText, icon }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const Icon = icon ? createElement(icon, { size: 24, color: 'rgba(133, 153, 174, 1)' }) : null

  return (
    <UploadWrapper>
      {Icon}
      <UploadDescriptions>
        <Typography color={Colors.blueDark} fontWeight={700} fontSize={13}>
          {uploadText}
        </Typography>
        <FileInput type="file" onChange={onChange} accept={accept} ref={inputRef} />
        <Button
          onClick={() => {
            if (!inputRef.current) {
              return
            }

            inputRef.current?.click()
          }}>
          <Upload size={16} /> {uploadButtonText}
        </Button>
      </UploadDescriptions>
    </UploadWrapper>
  )
}

type FileUploadProps = {
  onChange?: (file: File | undefined) => void
  value?: File
  accept?: string
  uploadText?: string
  uploadButtonText?: string
  icon?: EmotionIcon
}

export const FileUpload: FC<FileUploadProps> = ({ onChange, value, accept, uploadText, uploadButtonText, icon }) => {
  const [dragging, setDragging] = useState(false)

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = () => {
    setDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)

    const file = e.dataTransfer.files[0]
    const validTypes = accept?.split(',') ?? []

    if (validTypes.length > 0) {
      for (const type of validTypes) {
        if (file.type === FILE_TYPES[type as keyof typeof FILE_TYPES]) {
          onChange?.(file)
          break
        }
      }
    } else {
      onChange?.(file)
    }
  }

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    onChange?.(file)
  }

  return (
    <Container
      style={{ backgroundColor: dragging ? Colors.blueLight : Colors.primaryBlue50 }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      <UploadContent
        onChange={handleFileInput}
        accept={accept}
        uploadText={uploadText}
        uploadButtonText={uploadButtonText}
        icon={icon}
      />
    </Container>
  )
}
